import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { HeadTag } from 'shared/components';
import { Box, CircularProgress, Stack, SvgIcon } from '@mui/material';
import BasicInformationForm from './components/BasicInformationForm';
import ClassificationDetailForm from './components/ClassificationDetailForm';
import AssigmentForm from './components/AssigmentForm';
import RemediationInformationForm from './components/RemediationInformationForm';
import {
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
} from 'shared/components/AppComponents';
import { ReactComponent as Plus } from 'assets/images/ic_plus.svg';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from 'shared/components/PageHeader';
import { Formik, FormikHelpers } from 'formik';
import { VulnerabilitySchema } from 'validators/Vulnerability';
import ScrollToError from 'shared/components/ScrollToError';
import { VulnerabilityService } from 'services/VulnerabilityService';
import { Toast, Utils } from 'shared/helpers';

const FormVulnerability = (): ReactElement => {
  const navigate = useNavigate();
  const { vulnerabilityId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const isEdit = useMemo(() => !!vulnerabilityId, [vulnerabilityId]);
  const [vulnerabilityDetail, setVulnerabilityDetail] = useState({
    title: '',
    vulnerability_category: 'Other',
    risk_rating: 'Low',
    owner_assignee: '',
    status: 'Triaged',
    confidentiality_classification: 'Internal Only',
    environment: 'Test',
    description: '',
    reporter_source: '',
    lifecycle_stage_impacted: '',
    exploitability_details: '',
    severity_score: '',
    business_impact: '',
    target_remediation_date: '',
    verification_evidence: '',
    residual_risk: '',
    compliance_notes: '',
    deployment_endpoints: [],
  });

  // API Calls
  const loadDetail = (id: string): void => {
    setIsLoading(true);
    VulnerabilityService.detail(id)
      .then((response) => {
        const { data } = response.data;
        if (data) {
          // data.assigned_to = data.assigned_to._id;
          setVulnerabilityDetail(data);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
        navigate(-1);
      });
  };

  const createVulnerability = (payload: any, callback: any): void => {
    VulnerabilityService.create(payload)
      .then((response) => {
        callback(response);
      })
      .catch((error) => {
        callback(false);
        Utils.handleErrorResponse(error);
      });
  };
  const updateVulnerability = (payload: any, callback: any): void => {
    VulnerabilityService.update(`${vulnerabilityId}`, payload)
      .then((response) => {
        callback(response);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
        callback(false);
      });
  };

  // UI Actions
  const actionOnSubmit = (
    formValues: any,
    fomikHelpers: FormikHelpers<any>
  ): void => {
    if (isEdit) {
      updateVulnerability(formValues, (response: any) => {
        fomikHelpers.setSubmitting(false);
        if (typeof response === 'object') {
          Toast.showSuccessMessage(
            'The vulnerability has been successfully updated.'
          );
          navigate(-1);
        }
      });
    } else {
      createVulnerability(formValues, (response: any) => {
        fomikHelpers.setSubmitting(false);
        if (typeof response === 'object') {
          Toast.showSuccessMessage(
            'The vulnerability has been successfully created.'
          );
          navigate(-1);
        }
      });
    }
  };

  // Hooks
  useEffect(() => {
    if (vulnerabilityId) {
      loadDetail(vulnerabilityId);
    }
  }, [vulnerabilityId]);

  // UI Elements
  return (
    <>
      {isLoading && (
        <Box className="empty-content">
          I<CircularProgress />
        </Box>
      )}
      {!isLoading && (
        <>
          <PageHeader
            title={isEdit ? 'Edit Vulnerability' : 'Create Vulnerability'}
          />
          <Box className="vulnerability form-create-vulnerability">
            <HeadTag title="Create Training Programs" />
            <Formik
              enableReinitialize
              initialValues={vulnerabilityDetail}
              validationSchema={VulnerabilitySchema}
              onSubmit={(formValues, formik) =>
                actionOnSubmit(formValues, formik)
              }
            >
              {({ handleSubmit, isSubmitting }) => (
                <>
                  <Box className="create-vulnerability-wrapper">
                    <ScrollToError />
                    <BasicInformationForm />
                    <ClassificationDetailForm test={undefined} />
                    <AssigmentForm test={undefined} />
                    <RemediationInformationForm test={undefined} />
                  </Box>
                  <Stack
                    direction="row-reverse"
                    className="button-vulnerability-group"
                  >
                    <AppPrimaryButton
                      isLoading={isSubmitting}
                      onClick={handleSubmit}
                      title={isEdit ? 'Update' : 'Create Vulnerability'}
                      startIcon={
                        isEdit ? (
                          <></>
                        ) : (
                          <SvgIcon
                            component={Plus}
                            inheritViewBox
                            className="view-icon"
                            fontSize="inherit"
                            htmlColor="#76A81F"
                          />
                        )
                      }
                    />
                    <AppPrimaryButtonOutlined
                      title="Cancel"
                      onClick={() => navigate('/vulnerabilities')}
                    />
                  </Stack>
                </>
              )}
            </Formik>
          </Box>
        </>
      )}
    </>
  );
};

export default FormVulnerability;
